import { useEffect, useRef, useState } from 'react';
import { Button } from '@jeraisonne/ui';
import { RoomPage, type VisioRole } from '@jeraisonne/visio';
import { supabase, supabaseUrl } from '@/lib/supabase';
import { appUrl } from '@/lib/app-url';
import { parseJoinUrl, redeemHandoff } from '@/features/handoff/redeem';
import { parseInviteUrl } from '@/features/invite/parse-invite-url';
import { InvitePage } from '@/features/invite/invite-page';
import { buildSameOriginInviteUrl } from '@/lib/invite-url';

type Screen =
  | { kind: 'redeeming' }
  | { kind: 'missing_token' }
  | { kind: 'invalid_or_expired' }
  | { kind: 'network_error' }
  | { kind: 'ready'; roomId: string; userId: string; role: VisioRole; displayName: string };

function ErrorScreen({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-6 text-center sm:py-8">
      <p className="text-ink-900 text-lg font-semibold">{title}</p>
      <p className="text-ink-500 max-w-sm text-sm">{body}</p>
      <Button asChild className="min-h-[2.75rem] w-full max-w-xs">
        <a href={appUrl()}>Retour à JeRaisonne</a>
      </Button>
    </div>
  );
}

/**
 * Point d'entrée — pas de router : deux flux distincts partagent l'origine
 * `tutor.jeraisonne.fr` selon le chemin. `/invite/:token` (Phase D, migration
 * 0259, lien RÉUTILISABLE — compte réel ou invité anonyme) est vérifié
 * AVANT tout hook de `HandoffJoinApp` (`/join/:roomId#token=`, jeton de
 * handoff SSO à USAGE UNIQUE) : un branchement conditionnel entre deux
 * composants distincts, jamais un retour anticipé au milieu des hooks d'un
 * même composant (règle des hooks).
 */
export function App() {
  const inviteToken = parseInviteUrl(window.location.pathname);
  if (inviteToken) return <InvitePage token={inviteToken} />;
  return <HandoffJoinApp />;
}

function HandoffJoinApp() {
  const [screen, setScreen] = useState<Screen>({ kind: 'redeeming' });
  // Le jeton de handoff est à USAGE UNIQUE : React StrictMode (dev) double-
  // invoque cet effet au montage (monte→démonte→remonte, MÊME instance —
  // useRef survit). Cette garde synchrone empêche un second appel réseau qui
  // perdrait la course contre le premier (déjà consommé côté serveur).
  //
  // ⚠️ Pas de flag `cancelled` classique ici : le cleanup simulé par
  // StrictMode s'exécute IMMÉDIATEMENT après l'invocation de l'effet, AVANT
  // que la chaîne async ait fini — un flag `cancelled` se ferait donc poser à
  // `true` par le cleanup de sa PROPRE invocation avant même d'avoir pu
  // appeler `setScreen`, alors qu'aucun démontage réel n'a eu lieu. `setState`
  // après un démontage réel est un no-op silencieux depuis React 18 — pas
  // besoin de s'en prémunir manuellement pour un effet qui ne s'exécute
  // qu'une fois dans la vie du composant.
  const redeemStarted = useRef(false);

  useEffect(() => {
    const { roomId, token } = parseJoinUrl(window.location.pathname, window.location.hash);

    if (!roomId || !token) {
      setScreen({ kind: 'missing_token' });
      return;
    }

    if (redeemStarted.current) return;
    redeemStarted.current = true;

    void (async () => {
      const result = await redeemHandoff(token);
      if (result.status !== 'ok') {
        setScreen({ kind: result.status });
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setScreen({ kind: 'invalid_or_expired' });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, role')
        .eq('id', user.id)
        .maybeSingle();

      const role: VisioRole =
        profile?.role === 'tutor' || profile?.role === 'admin' ? 'tutor' : 'student';
      setScreen({
        kind: 'ready',
        roomId,
        userId: user.id,
        role,
        displayName: profile?.display_name ?? '',
      });
    })();
  }, []);

  if (screen.kind === 'redeeming') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-4 py-6 sm:py-8">
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (screen.kind === 'missing_token') {
    return (
      <ErrorScreen
        title="Lien invalide"
        body="Ce lien de salle est incomplet. Reviens sur JeRaisonne et clique à nouveau sur « Rejoindre la salle »."
      />
    );
  }

  if (screen.kind === 'invalid_or_expired') {
    return (
      <ErrorScreen
        title="Lien expiré"
        body="Ce lien a déjà été utilisé ou a expiré. Reviens sur JeRaisonne et clique à nouveau sur « Rejoindre la salle »."
      />
    );
  }

  if (screen.kind === 'network_error') {
    return (
      <ErrorScreen
        title="Connexion impossible"
        body="Vérifie ta connexion internet et réessaie depuis JeRaisonne."
      />
    );
  }

  return (
    <RoomPage
      roomId={screen.roomId}
      me={{ userId: screen.userId, role: screen.role, displayName: screen.displayName }}
      db={supabase}
      supabaseUrl={supabaseUrl}
      onExit={() => {
        window.location.href = appUrl();
      }}
      buildInviteUrl={buildSameOriginInviteUrl}
    />
  );
}
